/**
 * Custom hook for renaming conversations with optimistic updates
 * Follows the same patterns as useSendMessage for consistency
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { 
  renameConversation, 
  RenameConversationParams, 
  RenameConversationResponse,
  conversationQueryKeys,
  ConversationApiError,
  type Conversation,
} from '../api/conversations';
import { ApiError } from '../api/base/errors';
import { useConversationStore } from '../stores/conversationStore';

/**
 * Optimistic update context for rollback on error
 */
interface OptimisticUpdateContext {
  previousConversationLists: Conversation[] | undefined;
  previousConversationDetail: any;
  previousTitle: string;
  newTitle: string;
  conversationId: string;
}

/**
 * Core mutation hook for renaming conversations
 * Implements optimistic updates with rollback following TanStack Query best practices
 */
export function useRenameConversation() {
  const queryClient = useQueryClient();
  
  // Get store actions for optimistic updates
  const updateConversationAction = useConversationStore(
    (state) => state.updateConversation
  );
  
  return useMutation({
    mutationFn: (params: RenameConversationParams) => renameConversation(params),
    
    // Optimistic updates following TanStack Query patterns
    onMutate: async (variables): Promise<OptimisticUpdateContext> => {
      const { conversationId, newTitle } = variables;
      
      // Cancel any outgoing refetches to prevent overwrites
      await queryClient.cancelQueries({ 
        queryKey: conversationQueryKeys.list({}) 
      });
      await queryClient.cancelQueries({ 
        queryKey: conversationQueryKeys.detail(conversationId) 
      });
      
      // Snapshot previous state for rollback
      const previousConversationLists = queryClient.getQueryData<Conversation[]>(
        conversationQueryKeys.list({})
      );
      
      const previousConversationDetail = queryClient.getQueryData(
        conversationQueryKeys.detail(conversationId)
      );
      
      // Get previous title for rollback
      const previousTitle = previousConversationLists?.find(
        (conv) => conv.conversationID === conversationId
      )?.conversationTitle || '';
      
      // Optimistically update conversation list
      if (previousConversationLists) {
        queryClient.setQueryData(
          conversationQueryKeys.list({}),
          (old: Conversation[] | undefined) => {
            if (!old) return old;
            
            return old.map((conversation) =>
              conversation.conversationID === conversationId
                ? { ...conversation, conversationTitle: newTitle }
                : conversation
            );
          }
        );
      }
      
      // Optimistically update conversation detail if it exists
      if (previousConversationDetail) {
        queryClient.setQueryData(
          conversationQueryKeys.detail(conversationId),
          (old: any) => {
            if (!old) return old;
            return {
              ...old,
              // Update any title fields that might exist in the detail
              conversationTitle: newTitle,
            };
          }
        );
      }
      
      // Update Zustand store optimistically
      updateConversationAction(conversationId, { 
        conversationTitle: newTitle 
      });
      
      return {
        previousConversationLists,
        previousConversationDetail,
        previousTitle,
        newTitle,
        conversationId,
      };
    },
    
    // Success handling - minimal work since optimistic updates are already applied
    onSuccess: (response: RenameConversationResponse, variables: RenameConversationParams) => {
      // The optimistic updates should be correct, so we mainly need to ensure
      // the cache is properly marked as fresh
      
      // Optionally, we could update with server response if needed
      // For now, we trust our optimistic update is correct
      
    },
    
    // Error handling with complete rollback
    onError: (error: Error, variables: RenameConversationParams, context?: OptimisticUpdateContext) => {
      if (!context) return;
      
      // Rollback conversation list
      if (context.previousConversationLists) {
        queryClient.setQueryData(
          conversationQueryKeys.list({}),
          context.previousConversationLists
        );
      }
      
      // Rollback conversation detail
      if (context.previousConversationDetail) {
        queryClient.setQueryData(
          conversationQueryKeys.detail(context.conversationId),
          context.previousConversationDetail
        );
      }
      
      // Rollback Zustand store
      updateConversationAction(context.conversationId, { 
        conversationTitle: context.previousTitle 
      });
      
      // Log error for debugging
      console.error('Failed to rename conversation:', {
        conversationId: variables.conversationId,
        error: error.message,
        rollbackTo: context.previousTitle,
      });
    },
    
    // Invalidate queries on completion (success or error)
    onSettled: (data, error, variables) => {
      // Mark conversation queries as stale to trigger eventual consistency
      queryClient.invalidateQueries({ 
        queryKey: conversationQueryKeys.list({}),
        refetchType: 'none' // Mark as stale but don't refetch immediately
      });
      
      // Also invalidate the specific conversation detail
      queryClient.invalidateQueries({ 
        queryKey: conversationQueryKeys.detail(variables.conversationId),
        refetchType: 'none'
      });
    },
    
    // Retry configuration following established patterns
    retry: (failureCount: number, error: any) => {
      // Don't retry authentication errors
      if (error instanceof ConversationApiError && error.status === 401) {
        return false;
      }
      
      // Don't retry client errors (4xx)
      if (error instanceof ApiError && error.status && error.status >= 400 && error.status < 500) {
        return false;
      }
      
      // Retry network errors and server errors up to 2 times
      return failureCount < 2;
    },
    
    // Exponential backoff for retries
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });
}

/**
 * High-level hook that provides a clean interface for components
 * Follows the same pattern as useChatActions
 */
export function useConversationRenameActions() {
  const renameMutation = useRenameConversation();
  
  const renameConversation = useCallback(async (
    conversationId: string,
    newTitle: string
  ) => {
    if (!conversationId.trim()) {
      throw new Error('Conversation ID is required');
    }
    
    if (!newTitle.trim()) {
      throw new Error('New title cannot be empty');
    }
    
    try {
      const response = await renameMutation.mutateAsync({
        conversationId: conversationId.trim(),
        newTitle: newTitle.trim(),
      });
      
      return response;
    } catch (error) {
      // Error is handled by mutation's onError
      throw error;
    }
  }, [renameMutation]);
  
  return {
    renameConversation,
    isLoading: renameMutation.isPending,
    error: renameMutation.error,
    isSuccess: renameMutation.isSuccess,
    isError: renameMutation.isError,
    reset: renameMutation.reset,
    // Helper computed properties
    canRename: !renameMutation.isPending,
  };
}